import produce from 'immer';
import {
  CreateSavedSearchApiArg,
  ReadAdvancedSearchResultsApiArg,
  ReadSearchApiArg,
  SearchResultType,
  UpdateSavedSearchApiArg,
} from 'lib-shared-types-api';
import { SearchParams } from 'lib-shared-url';
import moment from 'moment-timezone';

import { isDateCondition } from 'app/views/layout/views/search/utils/date/isDateCondition';
import { sanitizeHtml } from 'utils/sanitizeHtml';

/**
 * Creates read search results endpoint search params.
 *
 * @param type The search result type.
 * @param arg The read search api arg.
 */
export function createReadSearchResultsSearchParams(
  type: SearchResultType,
  arg: ReadSearchApiArg,
): SearchParams {
  const strict = arg.strict;
  const facets = arg.filter?.facets;
  const facetFilters = facets
    ? Object.keys(facets)
        .map((key) =>
          [...new Set<string>(facets[key])]
            .map((value) => [key, value].join(':'))
            .join(','),
        )
        .join(',')
    : undefined;

  return {
    scope: type,
    strict,
    facetFilters,
  };
}

export function transformReadAdvancedSearchResultsArg({
  type,
  params,
}: ReadAdvancedSearchResultsApiArg): ReadAdvancedSearchResultsApiArg {
  return {
    type,
    params: produce(params, (draftParams) => {
      if (draftParams.filters) {
        draftParams.filters.conditions.forEach((condition) => {
          if (isDateCondition(condition)) {
            condition.meta = { timezone: moment.tz.guess() };
          }
        });
      }

      // Pagination is 1-based in the frontend and 0-based in the backend
      draftParams.page = (draftParams.page ?? 1) - 1;
    }),
  };
}

export function transformCreateSavedSearchArg(
  arg: CreateSavedSearchApiArg,
): CreateSavedSearchApiArg {
  return {
    ...arg,
    name: sanitizeHtml(arg.name, 'plainText'),
  };
}

export function transformUpdateSavedSearchArg(
  arg: UpdateSavedSearchApiArg,
): UpdateSavedSearchApiArg {
  return {
    ...arg,
    saved_search: {
      ...arg.saved_search,
      name: sanitizeHtml(arg.saved_search.name, 'plainText'),
    },
  };
}
